/**
 * Standalone full-page form preview, mounted at /forms/preview/:templateId
 * via pathname routing in app.tsx. Lets staff open a form in a new tab to
 * see exactly what members will see, with design fidelity matching the
 * live editor. Inputs are interactive but nothing is saved.
 *
 * The org's resolved brand theme is applied here too — this page exists to show
 * staff what a member sees, and until TRUS-545 Phase 0 it was the one surface
 * that previewed a form's design without its design.
 */

import React, { useEffect, useState } from 'react';
import { Loader2, AlertCircle, ArrowLeft, Eye, Printer } from 'lucide-react';
import { formsAPI, FormTemplate } from '@/services/api/forms';
import { FormRenderer } from './FormRenderer';
import { FormSectionNav, wantsSectionNav } from './FormSectionNav';
import { useResolvedFormTheme } from '@/hooks/useResolvedFormTheme';
import { buildFormPrintHtml, printInNewWindow } from './formPrintBuilder';

interface FormPreviewPageProps {
  templateId: number;
  version?: number;
}

export default function FormPreviewPage({ templateId, version }: FormPreviewPageProps) {
  const [template, setTemplate] = useState<FormTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldData, setFieldData] = useState<Record<string, any>>({});
  const theme = useResolvedFormTheme(template?.design_template_id);
  // Opt-in per TEMPLATE, which is already per-org because a template carries cbo_id.
  const hasNav = wantsSectionNav((template as any)?.settings);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    formsAPI
      .getFormTemplate(templateId, version)
      .then((t) => {
        if (!cancelled) setTemplate(t);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || 'Failed to load form');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [templateId, version]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Preview Unavailable
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {error || 'Form template not found'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6 px-4">
      {/* max-w-3xl (768px) used to cap the form whatever the theme asked for:
          IFR's design template sets layout.max_width = 960px and it never took
          effect, so a page the design lays out at 1180px rendered in a 768px
          column. A form with a navigation rail needs the room for both, so the
          container widens only for those; every other preview keeps the width it
          has always had. FormRenderer still honours --wf-max-width INSIDE this. */}
      <div className={`${hasNav ? 'max-w-[1180px]' : 'max-w-3xl'} mx-auto`}>
        <div className="mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => window.close()}
            className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
          >
            <ArrowLeft className="w-4 h-4" /> Close preview
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() =>
                printInNewWindow(
                  buildFormPrintHtml({ template, mode: 'hard-copy-blank' })
                )
              }
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-medium"
              title="Print a blank hard-copy version: dropdowns and radios render as checkboxes for hand-fill"
            >
              <Printer className="w-3.5 h-3.5" /> Print blank (hard-copy)
            </button>
            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-medium">
              <Eye className="w-3 h-3" /> Preview mode · v{template.version}
            </span>
          </div>
        </div>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{template.name}</h1>
          {template.description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{template.description}</p>
          )}
        </div>

        {/* NO `items-start` HERE, deliberately.
            A sticky element can only travel inside its parent's box. With
            items-start the rail's grid cell shrinks to the rail's own height, so
            `sticky top-4` had nowhere to go and scrolled away with the page —
            the user had to return to the top to reach another section.
            The grid's default align-items: stretch makes the cell as tall as the
            row (i.e. as tall as the form), which is the room the rail needs. */}
        <div className={hasNav ? 'grid gap-4 lg:grid-cols-[230px_minmax(0,1fr)]' : undefined}>
        {hasNav && (
          // Hidden below `lg`: a 230px rail beside a form on a phone leaves
          // neither enough room. The sections are collapsible, which is the
          // small-screen answer.
          <div className="hidden lg:block">
            <FormSectionNav sections={template.sections || []} />
          </div>
        )}
        <FormRenderer
          sections={template.sections || []}
          value={fieldData}
          onChange={(id, v) => setFieldData((prev) => ({ ...prev, [id]: v }))}
          theme={theme}
          showSubmitterBlock
          banner={
            <div className="rounded-md bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-700/50 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
              Preview mode — entries are not saved. The Name / Email / Phone block above is always shown on the live public form.
            </div>
          }
        />
        </div>
      </div>
    </div>
  );
}
